import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  candidateAnswerGroupsForPage,
  fetchJson,
  hitTestGroupIndex,
  neighborPage,
  pageFilename,
  pageIndexFromFilename,
} from '../../lib/mathOcrUi';

function drawMapperGroups({ canvas, img, groups, hoverIndex }) {
  const ctx = canvas?.getContext('2d');
  if (!ctx || !img) return;

  canvas.width = Math.floor(img.clientWidth || 1);
  canvas.height = Math.floor(img.clientHeight || 1);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const sx = canvas.width / (img.naturalWidth || 1);
  const sy = canvas.height / (img.naturalHeight || 1);

  for (let i = 0; i < (groups || []).length; i += 1) {
    const hovered = i === hoverIndex;
    ctx.fillStyle = hovered ? 'rgba(59,130,246,0.25)' : 'rgba(34,197,94,0.14)';
    ctx.strokeStyle = hovered ? '#2563eb' : '#22c55e';
    ctx.lineWidth = hovered ? 3 : 2;

    for (const box of groups[i].boxes || []) {
      const [x1, y1, x2, y2] = box;
      ctx.fillRect(x1 * sx, y1 * sy, (x2 - x1) * sx, (y2 - y1) * sy);
      ctx.strokeRect(x1 * sx, y1 * sy, (x2 - x1) * sx, (y2 - y1) * sy);
    }
  }
}

function drawDrag({ canvas, img, s, e }) {
  const ctx = canvas?.getContext('2d');
  if (!ctx || !img) return;

  canvas.width = Math.floor(img.clientWidth || 1);
  canvas.height = Math.floor(img.clientHeight || 1);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (!s || !e) return;

  const x = Math.min(s.x, e.x);
  const y = Math.min(s.y, e.y);
  const w = Math.abs(s.x - e.x);
  const h = Math.abs(s.y - e.y);

  ctx.fillStyle = 'rgba(249,115,22,0.2)';
  ctx.strokeStyle = '#f97316';
  ctx.lineWidth = 2;
  ctx.fillRect(x, y, w, h);
  ctx.strokeRect(x, y, w, h);
}

export function MapperModal({
  open,
  mode,
  targetId,
  records,
  originalFiles,
  uploadFiles,
  currentJobId,
  currentFileStem,
  apiBaseUrl,
  onClose,
  onManualUpdated,
}) {
  const [view, setView] = useState('list'); // 'list' | 'page'
  const [selectedPage, setSelectedPage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [busyMessage, setBusyMessage] = useState('');

  // AI comparison state
  const [aiPhase, setAiPhase] = useState('crop'); // 'crop' | 'preview' | 'loading' | 'compare'
  const [originalAnswer, setOriginalAnswer] = useState('');
  const [aiExtractedText, setAiExtractedText] = useState('');
  const [compareIndex, setCompareIndex] = useState(0); // 0=original, 1=AI result
  const [cropPreviewB64, setCropPreviewB64] = useState('');  // base64 preview of cropped region

  // Per-file page images (for crop mode)
  const [filePageNames, setFilePageNames] = useState([]);

  const imgPrevRef = useRef(null);
  const cvsPrevRef = useRef(null);
  const imgCenterRef = useRef(null);
  const cvsCenterRef = useRef(null);
  const imgNextRef = useRef(null);
  const cvsNextRef = useRef(null);

  const isDraggingRef = useRef(false);
  const dragStartRef = useRef(null);
  const dragEndRef = useRef(null);

  const hoverIndexRef = useRef(-1);

  const targetRecord = useMemo(() => (records || []).find((r) => r.id === targetId) || null, [records, targetId]);

  const pages = useMemo(() => {
    if (filePageNames.length) {
      return filePageNames.map((fn) => {
        const m = fn.match(/page_(\d+)\.jpg$/);
        return m ? Number(m[1]) : null;
      }).filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
    }
    // Fallback to original/upload pages for 'choose' mode
    const files = (originalFiles && originalFiles.length) ? originalFiles : (uploadFiles || []);
    return files
      .map((f) => pageIndexFromFilename(f))
      .filter((x) => Number.isFinite(x))
      .sort((a, b) => a - b);
  }, [filePageNames, originalFiles, uploadFiles]);

  // Fetch per-file page images when the modal opens in crop mode
  useEffect(() => {
    if (!open) return;
    if (mode !== 'crop') { setFilePageNames([]); return; }
    if (!currentFileStem) return;
    fetchJson(`${apiBaseUrl}/api/file-pages/${encodeURIComponent(currentFileStem)}`)
      .then((data) => setFilePageNames(data.pages || []))
      .catch(() => setFilePageNames([]));
  }, [open, mode, currentFileStem, apiBaseUrl]);

  function getFilePageUrl(pi) {
    const fn = pageFilename(pi);
    return `${apiBaseUrl}/output/file-pages/${encodeURIComponent(currentFileStem)}/${fn}`;
  }

  // Legacy page URL for original_pages / uploads_pages
  function getLegacyPageUrl(pi, kind) {
    const fn = pageFilename(pi);
    if (kind === 'uploads') return `${apiBaseUrl}/output/uploads_pages/${currentJobId}/${fn}`;
    return `${apiBaseUrl}/output/original_pages/${fn}`;
  }

  const effectiveFiles = useMemo(() => {
    if (filePageNames.length) return filePageNames;
    if (originalFiles && originalFiles.length > 0) return originalFiles;
    if (uploadFiles && uploadFiles.length > 0) return uploadFiles;
    return [];
  }, [filePageNames, originalFiles, uploadFiles]);

  const effectiveKind = useMemo(() => {
    if (filePageNames.length) return 'file';
    if (originalFiles && originalFiles.length > 0) return 'original';
    return 'uploads';
  }, [filePageNames, originalFiles]);

  useEffect(() => {
    if (!open) return;

    setIsUpdating(false);
    setBusyMessage('');
    dragStartRef.current = null;
    dragEndRef.current = null;
    isDraggingRef.current = false;
    hoverIndexRef.current = -1;

    // Store original answer text for comparison
    setOriginalAnswer(targetRecord?.answer?.text || '');
    setAiPhase('crop');
    setAiExtractedText('');
    setCompareIndex(0);

    if (mode === 'choose') {
      const qPages = (targetRecord?.question?.page_indices || []).map(Number).filter((x) => Number.isFinite(x));
      if (qPages.length) {
        setSelectedPage(qPages[0]);
        setView('page');
        return;
      }
    }

    setSelectedPage(null);
    setView('list');
  }, [open, mode, targetId, targetRecord]);

  const centerPage = useMemo(() => {
    if (!pages.length) return null;
    if (selectedPage == null) return pages[0];
    return pages.includes(selectedPage) ? selectedPage : pages[0];
  }, [pages, selectedPage]);

  const prevPage = useMemo(() => {
    if (!pages.length || centerPage == null) return centerPage;
    return neighborPage(pages, centerPage, -1);
  }, [pages, centerPage]);

  const nextPage = useMemo(() => {
    if (!pages.length || centerPage == null) return centerPage;
    return neighborPage(pages, centerPage, +1);
  }, [pages, centerPage]);

  const candidates = useMemo(() => {
    if (!centerPage || !targetId) return [];
    if (mode !== 'choose') return [];
    return candidateAnswerGroupsForPage({ records, pageIndex: centerPage, targetId });
  }, [centerPage, targetId, mode, records]);

  function resolvePageUrl(pi) {
    if (effectiveKind === 'file') return getFilePageUrl(pi);
    return getLegacyPageUrl(pi, effectiveKind === 'uploads' ? 'uploads' : 'original');
  }

  // 'choose' mode mouse handling
  useEffect(() => {
    if (!open) return;
    if (mode !== 'choose') return;
    if (view !== 'page') return;

    const img = imgCenterRef.current;
    const canvas = cvsCenterRef.current;
    if (!img || !canvas) return;

    const draw = (hoverIndex) => drawMapperGroups({ canvas, img, groups: candidates, hoverIndex });

    const drawInitial = () => draw(hoverIndexRef.current);
    if (img.complete && img.naturalWidth > 0) {
      drawInitial();
    } else {
      img.onload = () => drawInitial();
    }

    const onMove = (ev) => {
      if (isUpdating) return;
      const idx = hitTestGroupIndex(img, canvas, candidates, ev.offsetX, ev.offsetY);
      if (idx !== hoverIndexRef.current) {
        hoverIndexRef.current = idx;
        draw(idx);
      }
    };

    const onClick = async (ev) => {
      if (isUpdating) return;
      const idx = hitTestGroupIndex(img, canvas, candidates, ev.offsetX, ev.offsetY);
      if (idx < 0) return;
      const src = candidates[idx];
      try {
        setIsUpdating(true);
        setBusyMessage('Updating manual answer...');
        await fetchJson(`${apiBaseUrl}/api/remap-answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ target_id: targetId, source_id: src.sourceId }),
        });
        await onManualUpdated?.({ targetId });
      } finally {
        setIsUpdating(false);
        setBusyMessage('');
        onClose?.();
      }
    };

    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('click', onClick);
    return () => {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('click', onClick);
    };
  }, [open, mode, view, candidates, apiBaseUrl, targetId, isUpdating, onManualUpdated, onClose]);

  // Crop mode mouse handling
  useEffect(() => {
    if (!open) return;
    if (mode !== 'crop') return;
    if (view !== 'page') return;
    if (aiPhase !== 'crop') return;

    const img = imgCenterRef.current;
    const canvas = cvsCenterRef.current;
    if (!img || !canvas) return;

    const drawNow = () => drawDrag({ canvas, img, s: dragStartRef.current, e: dragEndRef.current });

    const onDown = (ev) => {
      if (isUpdating) return;
      isDraggingRef.current = true;
      dragStartRef.current = { x: ev.offsetX, y: ev.offsetY };
      dragEndRef.current = { x: ev.offsetX, y: ev.offsetY };
      drawNow();
    };
    const onMove = (ev) => {
      if (!isDraggingRef.current) return;
      if (isUpdating) return;
      dragEndRef.current = { x: ev.offsetX, y: ev.offsetY };
      drawNow();
    };
    const finish = (ev) => {
      if (isUpdating) return;
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      dragEndRef.current = dragEndRef.current || { x: ev.offsetX, y: ev.offsetY };

      const s = dragStartRef.current;
      const e = dragEndRef.current;
      if (!s || !e) return;

      const x1 = Math.min(s.x, e.x);
      const y1 = Math.min(s.y, e.y);
      const x2 = Math.max(s.x, e.x);
      const y2 = Math.max(s.y, e.y);
      if (x2 - x1 < 8 || y2 - y1 < 8) return;

      // Scale from display coordinates to natural image coordinates
      const scaleX = (img.naturalWidth || 1) / Math.max(1, canvas.clientWidth || 1);
      const scaleY = (img.naturalHeight || 1) / Math.max(1, canvas.clientHeight || 1);

      // Crop the image client-side and generate base64 preview
      const offCanvas = document.createElement('canvas');
      const sx = Math.round(x1 * scaleX);
      const sy = Math.round(y1 * scaleY);
      const sw = Math.round((x2 - x1) * scaleX);
      const sh = Math.round((y2 - y1) * scaleY);
      offCanvas.width = sw;
      offCanvas.height = sh;
      const ctx2d = offCanvas.getContext('2d');
      try {
        ctx2d.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        const dataUrl = offCanvas.toDataURL('image/png');
        setCropPreviewB64(dataUrl);
        setAiPhase('preview');
      } catch (err) {
        // Likely a tainted canvas — alert the user
        window.alert('Cannot crop this image. The image may be from a cross-origin source without CORS headers.');
      }
    };

    const onUp = (ev) => finish(ev);
    const onLeave = (ev) => finish(ev);

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup', onUp);
    canvas.addEventListener('mouseleave', onLeave);

    if (img.complete && img.naturalWidth > 0) drawNow();
    else img.onload = drawNow;

    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [open, mode, view, apiBaseUrl, targetId, centerPage, isUpdating, onManualUpdated, onClose, aiPhase]);

  const handleAcceptAi = async () => {
    try {
      setIsUpdating(true);
      setBusyMessage('Saving AI answer...');
      await fetchJson(`${apiBaseUrl}/api/manual/set-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_id: targetId,
          answer_text: aiExtractedText,
        }),
      });
      await onManualUpdated?.({ targetId });
    } catch (e) {
      window.alert(`Save failed: ${String(e)}`);
    } finally {
      setIsUpdating(false);
      setBusyMessage('');
      onClose?.();
    }
  };

  const handleRetryAi = () => {
    dragStartRef.current = null;
    dragEndRef.current = null;
    const canvas = cvsCenterRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setAiPhase('crop');
    setAiExtractedText('');
    setCompareIndex(0);
  };

  if (!open) return null;

  // Crop preview handler — send client-cropped image to backend
  const handleConfirmCrop = async () => {
    setAiPhase('loading');
    setBusyMessage('AI is extracting LaTeX...');
    setIsUpdating(true);
    try {
      const res = await fetchJson(`${apiBaseUrl}/api/crop-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_id: targetId,
          image_base64: cropPreviewB64,
        }),
      });
      if (res.error) {
        window.alert(`AI extraction failed: ${res.error}`);
        setAiPhase('preview');
        return;
      }
      setAiExtractedText(res.extracted_text || '');
      setAiPhase('compare');
      setCompareIndex(0);
    } catch (ex) {
      window.alert(`AI extraction failed: ${String(ex)}`);
      setAiPhase('preview');
    } finally {
      setIsUpdating(false);
      setBusyMessage('');
    }
  };

  const handleCancelCrop = () => {
    dragStartRef.current = null;
    dragEndRef.current = null;
    const canvas = cvsCenterRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setCropPreviewB64('');
    setAiPhase('crop');
  };

  // Crop preview view
  if (aiPhase === 'preview' && cropPreviewB64) {
    return (
      <div className="modal" onClick={onClose}>
        <div className="modalCard" onClick={(e) => e.stopPropagation()}>
          <div className="modalHead">
            <div className="modalTitle">Confirm Crop</div>
            <button type="button" className="ghost" onClick={handleCancelCrop}>Cancel</button>
          </div>
          <div className="modalBody" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '16px' }}>
            <div style={{ maxWidth: '100%', border: '2px solid #22c55e', borderRadius: '8px', overflow: 'hidden' }}>
              <img src={cropPreviewB64} alt="Cropped region" style={{ maxWidth: '100%', display: 'block' }} />
            </div>
            <div className="muted" style={{ fontSize: '0.9rem' }}>Review the cropped region above, then confirm to send to AI for LaTeX extraction.</div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={handleConfirmCrop} disabled={isUpdating}>
                ✅ Confirm & Extract
              </button>
              <button type="button" className="ghost" onClick={handleCancelCrop} disabled={isUpdating}>
                🔄 Re-crop
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading view
  if (aiPhase === 'loading') {
    return (
      <div className="modal" onClick={onClose}>
        <div className="modalCard" onClick={(e) => e.stopPropagation()}>
          <div className="modalHead">
            <div className="modalTitle">AI Extraction</div>
            <button type="button" className="ghost" onClick={onClose}>Close</button>
          </div>
          <div className="modalBody" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '16px' }}>
            <span className="spinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }} />
            <div className="muted" style={{ fontSize: '1.1rem' }}>AI is analyzing the cropped region...</div>
          </div>
        </div>
      </div>
    );
  }

  // Compare view
  if (aiPhase === 'compare') {
    const panes = [originalAnswer || '(empty)', aiExtractedText || '(empty)'];
    const labels = ['Original Answer', 'AI Extracted LaTeX'];

    return (
      <div className="modal" onClick={onClose}>
        <div className="modalCard" onClick={(e) => e.stopPropagation()}>
          <div className="modalHead">
            <div className="modalTitle">Compare: {labels[compareIndex]}</div>
            <button type="button" className="ghost" onClick={onClose}>Close</button>
          </div>
          <div className="modalBody">
            {isUpdating ? <div className="mapperBusy">{busyMessage}</div> : null}

            <div className="mapperCompareNav">
              <button type="button" className="mapperNav" disabled={compareIndex === 0} onClick={() => setCompareIndex(0)}>
                ⬅ Original
              </button>
              <span className="muted" style={{ fontSize: '0.85rem' }}>
                {compareIndex === 0 ? 'Original' : 'AI Result'}
              </span>
              <button type="button" className="mapperNav" disabled={compareIndex === 1} onClick={() => setCompareIndex(1)}>
                AI Result ➡
              </button>
            </div>

            <div className="mapperComparePane">
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.92rem', lineHeight: '1.5', margin: 0 }}>
                {panes[compareIndex]}
              </pre>
            </div>

            <div className="mapperCompareActions">
              <button type="button" onClick={handleAcceptAi} disabled={isUpdating}>
                ✅ Accept AI Result
              </button>
              <button type="button" className="ghost" onClick={handleRetryAi} disabled={isUpdating}>
                🔄 Retry
              </button>
              <button type="button" className="ghost" onClick={onClose} disabled={isUpdating}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const title =
    view === 'page'
      ? `page ${centerPage} — ${mode === 'crop' ? 'drag to crop answer' : 'hover/click to remap answer'}`
      : mode === 'crop'
        ? 'Crop answer: choose page'
        : 'Choose another question: choose page';

  return (
    <div className="modal" onClick={onClose}>
      <div className="modalCard" onClick={(e) => e.stopPropagation()}>
        <div className="modalHead">
          <button
            id="mapperBackBtn"
            type="button"
            className={view === 'list' ? 'ghost hidden' : 'ghost'}
            onClick={() => setView('list')}
          >
            ← Back
          </button>
          <div id="mapperTitle" className="modalTitle">{title}</div>
          <button id="mapperCloseBtn" type="button" className="ghost" onClick={onClose}>Close</button>
        </div>

        <div className="modalBody" id="mapperBody" style={{ position: 'relative' }}>
          {isUpdating ? <div className="mapperBusy">{busyMessage}</div> : null}

          {view === 'list' ? (
            <div className="pageGrid">
              {(effectiveFiles || []).map((fn) => {
                const nameOrFn = typeof fn === 'string' ? fn : String(fn);
                const pi = pageIndexFromFilename(nameOrFn);
                if (!Number.isFinite(pi)) return null;
                const src = resolvePageUrl(pi);
                return (
                  <div key={nameOrFn} className="pageTile" onClick={() => { setSelectedPage(pi); setView('page'); }}>
                    <img src={src} alt={`page ${pi}`} />
                    <div className="pageTileLabel">page {pi}</div>
                  </div>
                );
              })}
              {(!effectiveFiles || effectiveFiles.length === 0) ? (
                <div className="muted">No page images found. Process a job first.</div>
              ) : null}
            </div>
          ) : (
            <div className="mapperRow">
              <button type="button" className="mapperNav mapperNavLeft" disabled={prevPage === centerPage} onClick={() => setSelectedPage(prevPage)}>
                ←
              </button>

              <div className="mapperSlot">
                <div className="mapperPanel mapperPanelSmall">
                  <img ref={imgPrevRef} className="mapperImg" crossOrigin="anonymous" src={resolvePageUrl(prevPage)} alt={`page ${prevPage}`} />
                  <canvas ref={cvsPrevRef} className="mapperCanvas" />
                </div>
                <div className="pageTileLabel">page {prevPage}</div>
              </div>

              <div className="mapperSlot mapperSlotCenter">
                <div className="mapperPanel">
                  <img ref={imgCenterRef} className="mapperImg" crossOrigin="anonymous" src={resolvePageUrl(centerPage)} alt={`page ${centerPage}`} />
                  <canvas ref={cvsCenterRef} className="mapperCanvas" />
                </div>
                <div className="pageTileLabel">page {centerPage}</div>
              </div>

              <div className="mapperSlot">
                <div className="mapperPanel mapperPanelSmall">
                  <img ref={imgNextRef} className="mapperImg" crossOrigin="anonymous" src={resolvePageUrl(nextPage)} alt={`page ${nextPage}`} />
                  <canvas ref={cvsNextRef} className="mapperCanvas" />
                </div>
                <div className="pageTileLabel">page {nextPage}</div>
              </div>

              <button type="button" className="mapperNav mapperNavRight" disabled={nextPage === centerPage} onClick={() => setSelectedPage(nextPage)}>
                →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}